/* Customer portal — Registration screen. */
import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import GadtechLogo from "@/components/GadtechLogo";
import usePortalPWA from "@/hooks/usePortalPWA";

export default function PortalRegister() {
  usePortalPWA();
  const { register } = usePortalAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    email: "", password: "", name: "", phone: "",
    company_name: "", account_code: "",
  });
  const [busy, setBusy] = useState(false);
  const invitedEmail = params.get("email") || "";

  // Pre-fill from an invite link (email, company, account_code)
  useEffect(() => {
    setForm((f) => ({
      ...f,
      email: invitedEmail || f.email,
      company_name: params.get("company") || f.company_name,
      account_code: params.get("account_code") || f.account_code,
    }));
  }, [invitedEmail, params]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.email || !form.password) return toast.error("Email and password required");
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    try {
      const u = await register(form);
      if (u.status === "pending") {
        toast.success("Account created. Dispatch will review your account and unlock full features shortly.", { duration: 6000 });
      } else {
        toast.success("Account linked to " + (u.customer_name || "your customer record"));
      }
      nav("/portal/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white grid lg:grid-cols-2" data-testid="portal-register">
      <div className="p-10 lg:p-16 flex flex-col justify-center max-w-xl w-full mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="bg-white/95 rounded-lg px-3 py-2 shadow-lg shadow-orange-900/30">
            <GadtechLogo className="h-10" data-testid="portal-register-logo" />
          </div>
          <div className="text-[10px] tracking-[0.28em] text-neutral-400">CUSTOMER PORTAL</div>
        </div>

        <h1 className="text-4xl font-black leading-none mb-2">Create account</h1>
        <p className="text-neutral-400 text-sm mb-8">Two-minute setup. Existing customers will be linked automatically.</p>

        {invitedEmail && (
          <div className="mb-6 border border-primary-orange/40 bg-primary-orange/5 px-4 py-3 text-xs text-primary-orange tracking-wide" data-testid="portal-reg-invite-banner">
            You've been invited by ArmorBatch Global. We've pre-filled your details below —
            just pick a password to finish setup.
          </div>
        )}

        <form onSubmit={submit} className="space-y-4 max-w-md">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] tracking-widest text-neutral-400 uppercase">Name</Label>
              <Input data-testid="portal-reg-name" required value={form.name} onChange={set("name")} className="rounded-none bg-transparent border-neutral-700 text-white mono focus:border-primary-orange" />
            </div>
            <div>
              <Label className="text-[11px] tracking-widest text-neutral-400 uppercase">Phone</Label>
              <Input data-testid="portal-reg-phone" value={form.phone} onChange={set("phone")} className="rounded-none bg-transparent border-neutral-700 text-white mono focus:border-primary-orange" />
            </div>
          </div>
          <div>
            <Label className="text-[11px] tracking-widest text-neutral-400 uppercase">Email</Label>
            <Input data-testid="portal-reg-email" type="email" required value={form.email} onChange={set("email")} className="rounded-none bg-transparent border-neutral-700 text-white mono focus:border-primary-orange" />
          </div>
          <div>
            <Label className="text-[11px] tracking-widest text-neutral-400 uppercase">Password</Label>
            <Input data-testid="portal-reg-password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="rounded-none bg-transparent border-neutral-700 text-white mono focus:border-primary-orange" />
          </div>
          <div>
            <Label className="text-[11px] tracking-widest text-neutral-400 uppercase">Company name (optional)</Label>
            <Input data-testid="portal-reg-company" value={form.company_name} onChange={set("company_name")} placeholder="e.g. Smith Landscaping" className="rounded-none bg-transparent border-neutral-700 text-white mono focus:border-primary-orange" />
          </div>
          <div>
            <Label className="text-[11px] tracking-widest text-neutral-400 uppercase">Account code (optional)</Label>
            <Input data-testid="portal-reg-account-code" value={form.account_code} onChange={set("account_code")} placeholder="e.g. SBC-001 — leave blank if you don't have one" className="rounded-none bg-transparent border-neutral-700 text-white mono focus:border-primary-orange" />
          </div>

          <Button data-testid="portal-reg-submit" type="submit" disabled={busy} className="w-full rounded-none bg-primary-orange text-black hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-sm font-bold py-3 mt-4">
            <UserPlus className="w-4 h-4 mr-2" />
            {busy ? "Creating…" : "Create account"}
          </Button>
        </form>

        <div className="mt-6 text-sm text-neutral-400">
          Already have an account?{" "}
          <Link to="/portal/login" data-testid="portal-reg-to-login" className="text-primary-orange hover:underline font-bold">
            Sign in
          </Link>
        </div>
      </div>

      <div className="hidden lg:block relative">
        <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(rgba(10,10,11,0.55),rgba(10,10,11,0.85)), url('https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1600&q=80')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative z-10 h-full flex flex-col justify-end p-16 text-white">
          <div className="text-[10px] tracking-[0.28em] text-primary-orange mb-2">SELF-SERVICE</div>
          <div className="text-4xl font-black leading-tight mb-2">Everything your<br/>site foreman needs.</div>
          <div className="text-neutral-300 text-sm max-w-sm">Place orders, track ETAs, approve deliveries, download invoices — from any device.</div>
        </div>
      </div>
    </div>
  );
}
